import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Clock, MessageSquare, Save, ShieldCheck, Smartphone, Users } from 'lucide-react';
import {
  CONTRACTOR_CHECKPOINTS,
  CONTRACTOR_TEMPLATE_VARIABLES,
  ContractorCheckpoint,
  ContractorReadiness,
  ContractorStatusConfig,
  DEFAULT_CONTRACTOR_STATUS_CONFIG,
  LoadedContractorStatusConfig,
  cloneContractorStatusConfig,
  loadContractorStatusConfig,
  loadContractorStatusReadiness,
  saveContractorStatusConfig,
  validateContractorStatusConfig
} from '../contractorStatusNotifications';
import { smsMetrics } from '../webSms';
import { Button } from './ui/Button';
import { GlassCard } from './ui/GlassCard';

const checkpointDetails: Record<ContractorCheckpoint, { label: string; anchor: string }> = {
  ARRIVED: { label: 'Arrived on Site', anchor: 'Explicit contractor call time' },
  SETUP_COMPLETE: { label: 'Setup Complete', anchor: 'Shift setup time' },
  PACK_DOWN: { label: 'Pack Down Complete', anchor: 'Shift pack-down time' },
  SHIFT_END: { label: 'Shift Complete / Released', anchor: 'Shift end time' }
};

const metricLabels: Array<[keyof ContractorReadiness, string]> = [
  ['upcoming_confirmed_assignments', 'Confirmed assignments'],
  ['upcoming_confirmed_portal_contractors', 'Portal contractors'],
  ['valid_mobile_count', 'Valid NZ mobiles'],
  ['missing_or_invalid_mobile_count', 'Missing/invalid mobiles'],
  ['unresolved_roster_members', 'Unresolved roster members'],
  ['shifts_missing_timezone', 'Shifts missing timezone'],
  ['shifts_missing_call_time', 'Missing call times'],
  ['shifts_missing_setup_time', 'Missing setup times'],
  ['shifts_missing_pack_down_time', 'Missing pack-down times'],
  ['shifts_missing_end_time', 'Missing end times']
];

export const ContractorStatusNotificationSettings: React.FC<{ onBack: () => void; onSaved: () => Promise<void> }> = ({ onBack, onSaved }) => {
  const [saved, setSaved] = useState<LoadedContractorStatusConfig | null>(null);
  const [draft, setDraft] = useState<ContractorStatusConfig>(cloneContractorStatusConfig(DEFAULT_CONTRACTOR_STATUS_CONFIG));
  const [readiness, setReadiness] = useState<ContractorReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const errors = useMemo(() => validateContractorStatusConfig(draft), [draft]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [config, audit] = await Promise.all([loadContractorStatusConfig(), loadContractorStatusReadiness()]);
      setSaved(config);
      setDraft(cloneContractorStatusConfig(config || DEFAULT_CONTRACTOR_STATUS_CONFIG));
      setReadiness(audit);
    } catch (loadError: any) { setError(loadError.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const updateRule = (checkpoint: ContractorCheckpoint, patch: Partial<ContractorStatusConfig['rules'][ContractorCheckpoint]>) => {
    setDraft(current => ({
      ...current,
      rules: { ...current.rules, [checkpoint]: { ...current.rules[checkpoint], ...patch } }
    }));
  };

  const save = async () => {
    if (errors.length) return;
    setSaving(true); setError(''); setNotice('');
    try {
      const result = await saveContractorStatusConfig(saved?.version || 0, draft);
      await Promise.all([load(), onSaved()]);
      setNotice(`Configuration version ${result.version} saved. It can create shadow records only; contractor delivery remains disabled.`);
    } catch (saveError: any) { setError(saveError.message); }
    finally { setSaving(false); }
  };

  const inputClass = 'w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-brand-neonBlue disabled:cursor-not-allowed disabled:opacity-50';
  const fieldLabel = 'type-label mb-2 block text-slate-400';

  return <div className="space-y-6">
    <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <div>
        <button className="type-button mb-4 flex items-center gap-2 text-xs text-brand-neonBlue" onClick={onBack}><ArrowLeft size={14}/> Notifications</button>
        <h1 className="type-page-title text-white">Contractor Status Reminders</h1>
        <p className="mt-3 max-w-3xl text-sm text-slate-400">Configure SMS reminders that are evaluated in shadow mode only. No contractor delivery path exists in this phase.</p>
      </div>
      <span className="rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-xs font-bold uppercase tracking-wider text-violet-300">Shadow only · SMS off</span>
    </div>

    {error && <div role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error}</div>}
    {notice && <div role="status" className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-300">{notice}</div>}
    {errors.length > 0 && <div role="alert" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200"><ul className="list-disc space-y-1 pl-5">{errors.map(item => <li key={item}>{item}</li>)}</ul></div>}

    {loading ? <div role="status" className="py-16 text-center text-slate-500">Loading contractor reminder settings…</div> : <>
      <GlassCard className="space-y-6 p-6 md:p-8">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
          <div><h2 className="type-heading text-xl text-white">Shadow evaluation</h2><p className="mt-1 text-sm text-slate-500">Enabled means schedule and record shadow decisions. External SMS remains unavailable.</p></div>
          <label className="flex cursor-pointer items-center gap-3 text-sm font-bold text-white"><input aria-label="Contractor shadow evaluation enabled" type="checkbox" checked={draft.enabled} onChange={event => setDraft(current => ({ ...current, enabled: event.target.checked }))} className="h-5 w-5 accent-cyan-400"/> Enabled</label>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-slate-950/50 p-4"><Smartphone className="mb-3 text-brand-neonBlue" size={20}/><span className="type-label text-slate-500">Channel</span><p className="mt-1 font-bold text-white">SMS only</p></div>
          <div className="rounded-xl border border-white/10 bg-slate-950/50 p-4"><ShieldCheck className="mb-3 text-green-400" size={20}/><span className="type-label text-slate-500">Contractor delivery</span><p className="mt-1 font-bold text-white">Disabled</p></div>
          <div className="rounded-xl border border-white/10 bg-slate-950/50 p-4"><Users className="mb-3 text-amber-300" size={20}/><span className="type-label text-slate-500">Escalation</span><p className="mt-1 font-bold text-white">Unavailable</p></div>
        </div>
      </GlassCard>

      {readiness && <GlassCard className="space-y-5 p-6 md:p-8">
        <div><h2 className="type-heading text-xl text-white">Readiness audit</h2><p className="mt-1 text-sm text-slate-500">Counts only. Normalized mobile numbers are not displayed.</p></div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">{metricLabels.map(([key, text]) => <div key={key} className="rounded-xl border border-white/10 bg-slate-950/50 p-4"><span className="type-label text-slate-500">{text}</span><p className="mt-2 text-2xl font-black text-white">{readiness[key]}</p></div>)}</div>
        <div className="flex items-start gap-3 rounded-xl border border-green-500/20 bg-green-500/5 p-4 text-sm text-slate-300"><ShieldCheck className="mt-0.5 shrink-0 text-green-400" size={18}/><p>Kill switch ON · external SMS OFF · contractor delivery OFF. Missing data is skipped rather than inferred.</p></div>
      </GlassCard>}

      <div className="space-y-5">
        {CONTRACTOR_CHECKPOINTS.map(checkpoint => {
          const rule = draft.rules[checkpoint];
          const metrics = smsMetrics(rule.sms_body);
          return <GlassCard key={checkpoint} className="space-y-5 p-6 md:p-8">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start"><div><h2 className="type-heading text-xl text-white">{checkpointDetails[checkpoint].label}</h2><p className="mt-1 flex items-center gap-2 text-sm text-slate-500"><Clock size={14}/> Anchor: {checkpointDetails[checkpoint].anchor}</p></div><label className="flex items-center gap-3 text-sm font-bold text-white"><input aria-label={`${checkpoint} reminder enabled`} type="checkbox" checked={rule.enabled} onChange={event => updateRule(checkpoint, { enabled: event.target.checked })} className="h-5 w-5 accent-cyan-400"/> Reminder enabled</label></div>
            <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
              <div><label className={fieldLabel} htmlFor={`${checkpoint}-delay`}>Grace period after anchor</label><div className="relative"><input id={`${checkpoint}-delay`} className={inputClass} type="number" min="0" max="1440" value={rule.delay_minutes} onChange={event => updateRule(checkpoint, { delay_minutes: Number(event.target.value) })}/><span className="pointer-events-none absolute right-4 top-3 text-sm text-slate-500">minutes</span></div></div>
              <div><label className={fieldLabel} htmlFor={`${checkpoint}-body`}>SMS template</label><textarea id={`${checkpoint}-body`} className={`${inputClass} min-h-[120px] resize-y`} maxLength={1600} value={rule.sms_body} onChange={event => updateRule(checkpoint, { sms_body: event.target.value })}/><p className="mt-2 text-xs text-slate-500">{metrics.characters} characters · {metrics.segments} segment{metrics.segments === 1 ? '' : 's'} · {metrics.encoding}</p></div>
            </div>
            <div><p className={fieldLabel}>Supported variables</p><div className="flex flex-wrap gap-2">{CONTRACTOR_TEMPLATE_VARIABLES.map(variable => <button type="button" key={variable} className="rounded-lg border border-white/10 bg-slate-950 px-2.5 py-1.5 font-mono text-xs text-brand-neonBlue" onClick={() => updateRule(checkpoint, { sms_body: `${rule.sms_body}{{${variable}}}` })}>{`{{${variable}}}`}</button>)}</div></div>
          </GlassCard>;
        })}
      </div>

      <GlassCard className="space-y-3 border-amber-500/20 p-6 text-sm text-slate-300"><div className="flex items-center gap-2 font-bold text-amber-300"><MessageSquare size={18}/> Deliberately unavailable</div><p>EN_ROUTE requires an approved departure-time rule. GEAR_RETURNED requires an explicit return deadline. Event Manager escalation requires an authoritative manager resolver.</p></GlassCard>

      <div className="flex flex-wrap justify-end gap-3"><Button variant="outline" onClick={() => { setDraft(cloneContractorStatusConfig(saved || DEFAULT_CONTRACTOR_STATUS_CONFIG)); setError(''); setNotice(''); }}>Cancel</Button><Button disabled={saving || errors.length > 0} onClick={() => void save()}><Save size={15} className="mr-2"/>{saving ? 'Saving…' : 'Save immutable version'}</Button></div>
    </>}
  </div>;
};
